/**
 * Main entry point for the React bundle (built by Vite).
 *
 * This file:
 * 1. Bootstraps the global React providers (Theme, Toast, Modal)
 * 2. Exposes the public API on `window.OrnaReact` so legacy non-module
 *    code (CallCards.ts, CaseReportManagement.ts, etc.) can mount
 *    React components and use imperative APIs.
 */

// Vendor libraries — must be first (replaces LibraryScripts.js + Compiled.css)
import './vendor';
import './styles/vendor.css';

// Global registrations — must run BEFORE any module that reads from window.
// Order matters: each module captures window globals at module scope, so
// dependencies must be registered on window before dependents load.
import '@/services/app-compat';          // defines App on window
import '@/services/version-service';
import '@/services/theme-service';
import '@/services/pubnub-legacy-bridge';
import '@/services/global-events';
import '@/services/thumbnail-retry';         // reloads thumbnails that failed at the image resizer
import '@/constants/codes';              // defines Codes, ModuleType (needed by legacy-utils, models/legacy)
import '@/constants/formats';            // defines format constants (needed by legacy-utils)
import '@/utils/legacy-utils';           // defines hasValue, hasStringValue, etc. (needed by models/legacy, api-class)
import '@/services/api-class';           // defines api on window (needed by models/legacy)
import '@/models/legacy';                // defines entity types (needs hasValue, api, Codes)
import '@/services/app-bootstrap';       // registers bootstrapApp on load (needs api, App)
import '@/services/loading';
import '@/services/mdt-service';
import '@/services/cad-service';
import '@/services/panic-button';
import '@/services/admin-service';
import '@/services/keyboard-shortcuts';
import '@/services/noc-management';
import '@/services/mdt-module-entry';
import '@/services/windows-app-zoom';    // Windows-app zoom persistence (no-op in browsers)

// React bridge and public API imports
import {
  renderReactComponent,
  destroyReactComponent,
  mountGlobalProvidersWithRouter,
} from '@/react-bridge';
import { redirectLegacyUrl } from '@/router/legacy-url-redirect';
import { bootstrapApp } from '@/services/app-bootstrap';

import { showToast } from '@/contexts/ToastContext';
import { imperativeConfirm, imperativePrompt } from '@/contexts/ModalContext';
import { formatDate, formatDate2, formatDate3 } from '@/utils/date-format';
import * as api from '@/services/api';
import { navigationApi } from '@/services/navigation';
import * as caseReportService from '@/services/case-report-service';
import * as citationService from '@/services/citation-service';
import * as models from '@/models';
import * as utils from '@/utils';
import * as realtimeService from '@/services/azure-web-pubsub-service';
import { usePubNubEvent } from '@/hooks/usePubNubEvent';

// Side-effect imports: register base components on window
import '@/components/Tooltip';
import '@/components/ColorPicker';
import '@/components/FileDialog';
import '@/components/ImageDialog';
import '@/components/LinkDialog';
import '@/components/TableWizard';
import '@/components/Dropdownlist';
import '@/components/GroupedDropdownlist';
import '@/components/MultiSelect';
import '@/components/DateTimePicker';
import '@/components/TimePicker';
import '@/components/KstDataGrid';
import '@/components/AdminTabBar';
import '@/components/SignatureCanvas';

// Side-effect imports: modal components (register on window)
import '@/components/modals/AddStandardNocModal';
import '@/components/modals/AddStandardDispositionModal';
import '@/components/modals/AddStandardViolationTypeModal';
import '@/components/modals/ViewSopModal';
import '@/components/modals/PanicButtonModal';
import '@/components/modals/UserHelpModal';
import '@/components/modals/StatusUpdateModal';
import '@/components/modals/CancelSubscriptionModal';
import '@/components/modals/AddressSelectionModal';
import '@/components/modals/GeocodePreview';
import '@/components/LookupAddressButton';
import '@/components/screens/SupervisorAddressReviewScreen';
import '@/components/modals/AddVehicleModal';
import '@/components/modals/AddCallSignModal';
import '@/components/modals/AddCustomNocModal';
import '@/components/modals/AddSelfInitiateNocModal';
import '@/components/modals/AddEditCallAlertModal';
import '@/components/modals/EditCallAlertTypesModal';
import '@/components/modals/AddEditCustomLocationModal';
import '@/components/modals/AddCompanyDocumentModal';
import '@/components/modals/AddCustomDispositionModal';
import '@/components/modals/AddCustomViolationTypeModal';
import '@/components/modals/TimeUpdateModal';
import '@/components/modals/CallNotificationModal';
import '@/components/modals/OfficerAssignedDialog';
import '@/components/modals/PanicDialog';
import '@/components/modals/CancelPanicModal';
import '@/components/modals/CaseReportSubmittedDialog';
import '@/components/modals/ClientPortalAddEditClientModal';
import '@/components/modals/MileageModal';
import '@/components/modals/ThemePickerModal';
import '@/components/modals/ShortcutsModal';
import '@/components/modals/PageModal';
import '@/components/modals/CloseCallModal';
import '@/components/modals/EditSopModal';
import '@/components/modals/AddUserModal';
import '@/components/modals/SelfInitiateModal';
import '@/components/modals/MdtModuleEntryModal';
import '@/components/modals/CreateCustomGroupModal';
import '@/components/modals/SearchModal';

// Side-effect imports: standalone components (register on window)
import '@/components/EditorToolbar';
import '@/components/RichTextEditor';
import '@/components/OrnaToolbar';
import '@/components/ClientSitesGrid';
import '@/components/ClientNotificationsGrid';

// Side-effect imports: bridge screens not in screen registry (register on window)
import '@/constants/map-styles';
import '@/services/map-service';
import '@/components/screens/OfficersAssigned';
import '@/components/screens/MapScreen';

// Screen registrations — centralized registerScreen calls + screen imports
// (screens, NPSD, callcard, casereport, citation sub-components are all
// imported via the screen dependency chain)
import '@/router/screen-registrations';

// ── Public API exposed to legacy code ──────────────────────────────────

const OrnaReact = {
  // Bridge — mount/unmount React components from management modules
  renderComponent: renderReactComponent,
  destroyComponent: destroyReactComponent,

  // Imperative toast — drop-in replacement for App.showPopupNotification
  showToast,

  // Imperative modals — drop-in replacement for kstConfirm / kstPrompt
  confirm: imperativeConfirm,
  prompt: imperativePrompt,

  // Date formatting — replacement for Vue filters
  formatDate,
  formatDate2,
  formatDate3,

  // Promise-based API wrapper — Phase 1 of legacy migration
  api,

  // Navigation abstraction — Phase 2 of legacy migration
  navigate: navigationApi,

  // Entity models — Phase 3 of legacy migration
  models,

  // Utils decomposition — Phase 4 of legacy migration
  utils,

  // Case report service — Phase 6 of legacy migration
  caseReportService,

  // Citation service — Phase 6b of legacy migration
  citationService,

  // Realtime service — Phase 9 of legacy migration
  realtime: realtimeService,
  usePubNubEvent,
};

// Expose on window for non-module code
(window as any).OrnaReact = OrnaReact;

// ── Bootstrap global providers ─────────────────────────────────────────

function bootstrap() {
  // Create a hidden container for the global React providers.
  // This hosts the ToastContainer and registers imperative modal/toast APIs.
  let container = document.getElementById('orna-react-root');
  if (!container) {
    container = document.createElement('div');
    container.id = 'orna-react-root';
    // Insert inside the page layout (next to divContent) so registry screens
    // inherit the Bootstrap grid context (padding, etc.)
    const divContent = document.getElementById('divContent');
    if (divContent?.parentNode) {
      divContent.parentNode.insertBefore(container, divContent);
    } else {
      document.body.appendChild(container);
    }
  }

  // Run app bootstrap (createRoutes, version checks, etc.) BEFORE mounting
  // the router so that newVersionCheck and other globals are available
  // when RouteHandler first fires.
  bootstrapApp();

  // Convert any legacy query-string URL to clean pathname before mounting router
  redirectLegacyUrl();
  mountGlobalProvidersWithRouter(container);
}

// Bootstrap when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// All public API is exposed via `window.OrnaReact` above.
